// The always-mounted situational-awareness bridge (lives in DashboardLayout, NOT in ChatPage, so
// it works on every page and survives the chat becoming a dockable widget). It:
//   (a) keeps route / page / default-mode current via useRouteUIContext,
//   (b) responds to the agent's "get current state" request (`agent:get_state`), and
//   (c) proactively pushes a debounced snapshot whenever the visible context changes.
//
// Both paths emit `agent_realtime.state_sync` inside the existing `message` channel. That msg_type
// is the ONE the backend consumer (app/agents/consumer.py) already special-cases: it always
// persists the snapshot via merge_ui_context (so the NEXT prompt — text, interactive, or
// hands-free — reflects the latest screen) and, when a live realtime/hands-free session for this
// conversation exists, also nudges it immediately. This is what actually keeps interactive/
// hands-free mode's situational awareness in parity with text mode, which resends its own full
// snapshot (buildAgentContextMeta) with every turn. `session_id` must be the active conversation
// id (mirrors every other agent_realtime.* emit) — sourced from uiContext.activeConversationId,
// which ChatPage publishes since this hook lives outside ChatPage's tree. With no active
// conversation (chat isn't open) the backend just no-ops, same as before.
import { useEffect, useRef } from 'react';
import { useAppSelector } from '@/hooks/useAppSelector';
import { store } from '@/redux/common/store';
import { socketService } from '@/services/SocketService';
import { buildUIStateSnapshot } from '@/redux/selectors/buildUIStateSnapshot';
import { useRouteUIContext } from '@/hooks/useRouteUIContext';
import { useShellUIContext } from '@/hooks/useShellUIContext';

/** Inbound: agent → web request to send the current UI state. (Backend must emit this.) */
export const AGENT_GET_STATE_EVENT = 'agent:get_state';
/** Outbound `message` type carrying the snapshot back to the agent. */
export const AGENT_STATE_SYNC_TYPE = 'agent_realtime.state_sync';
/** Debounce window for proactive pushes — mirrors the onboarding voice settle interval. */
const PUSH_SETTLE_MS = 1500;

export function useUIContextBridge(): void {
  // (a) keep route/page/default-mode current, independent of chat, and publish the app shell
  // (sidebar nav, signed-in user, viewport / mobile-menu state).
  useRouteUIContext();
  useShellUIContext();

  // Cheap trigger for the proactive-push effect.
  const rev = useAppSelector((s) => s.uiContext.rev);
  const conversationId = useAppSelector((s) => s.uiContext.activeConversationId);

  // (b) respond to the agent's "get current state" request. Reads state at call time from the
  // store (not a stale closure). Idempotent under StrictMode via the on/off cleanup pair.
  useEffect(() => {
    const onGetState = () => {
      const state = store.getState();
      const snapshot = buildUIStateSnapshot(state);
      socketService.emit('message', {
        type: AGENT_STATE_SYNC_TYPE, reason: 'request', session_id: state.uiContext.activeConversationId, ...snapshot,
      });
    };
    socketService.on(AGENT_GET_STATE_EVENT, onGetState);
    return () => socketService.off(AGENT_GET_STATE_EVENT, onGetState);
  }, []);

  // (c) proactive push on change, debounced + de-duped (skip identical consecutive snapshots so
  // repeated renders don't spam the agent). The timeout is cleared on cleanup (StrictMode-safe).
  const lastSentRef = useRef<string>('');
  useEffect(() => {
    if (!conversationId) return;
    const t = setTimeout(() => {
      const snapshot = buildUIStateSnapshot(store.getState());
      const serialized = JSON.stringify(snapshot.ui); // compare the UI portion only
      if (serialized === lastSentRef.current) return;
      lastSentRef.current = serialized;
      socketService.emit('message', {
        type: AGENT_STATE_SYNC_TYPE, reason: 'push', session_id: conversationId, ...snapshot,
      });
    }, PUSH_SETTLE_MS);
    return () => clearTimeout(t);
  }, [rev, conversationId]);
}
